import React, { useState, useEffect } from 'react';
import { getWhatsappLink, createWhatsappLink, updateWhatsappLink, deleteWhatsappLink } from '../../../../../api/tiendaConfigAPI';
import Swal from 'sweetalert2';
import { Button, ButtonGroup, FormControl, InputGroup, ListGroup, Container } from 'react-bootstrap';
import { PencilFill, TrashFill } from 'react-bootstrap-icons';

const WhatsappLinkManager = () => {
    const [link, setLink] = useState(null);
    const [url, setUrl] = useState('');
    const [editing, setEditing] = useState(false);

    useEffect(() => {
        loadLink();
    }, []);

    const loadLink = async () => {
        try {
            const data = await getWhatsappLink();
            if (data.url) {
                setLink(data);
                setUrl(data.url);
            } else {
                setLink(null);
                setUrl('');
            }
        } catch (error) {
            console.error('Error al cargar el link de WhatsApp:', error);
            if (error.response?.status !== 404) {
                Swal.fire({
                    title: 'Error',
                    text: 'No se pudo cargar el link de WhatsApp.',
                    icon: 'error',
                    confirmButtonText: "Aceptar",
                    iconColor: '#da292e',
                    confirmButtonColor: "#da292e"
                });
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Verificar que la URL no esté vacía
        if (!url) {
            Swal.fire({
                title: 'Error',
                text: 'Por favor ingrese una URL válida.',
                icon: 'error',
                confirmButtonText: "Aceptar",
                iconColor: '#da292e',
                confirmButtonColor: "#da292e"
            });
            return;
        }

        try {
            if (editing) {
                // Actualizar el link
                await updateWhatsappLink(link.id, { url });
                Swal.fire({
                    title: 'Actualizado',
                    text: 'El link de WhatsApp se actualizó correctamente.',
                    icon: 'success',
                    confirmButtonText: 'Aceptar',
                    iconColor: '#2fb380',
                    confirmButtonColor: "#2c4cc4"
                });
            } else {
                // Crear un nuevo link
                await createWhatsappLink({ url });
                Swal.fire({
                    title: 'Éxito',
                    text: 'El link se ha añadido correctamente.',
                    icon: 'success',
                    confirmButtonText: 'Aceptar',
                    iconColor: '#2fb380',
                    confirmButtonColor: "#2c4cc4"
                });
            }

            setEditing(false);
            loadLink();
        } catch (error) {
            console.error('Error al guardar el link de WhatsApp:', error);
            Swal.fire({
                title: 'Error',
                text: 'Hubo un problema al guardar el link.',
                icon: 'error',
                confirmButtonText: "Aceptar",
                iconColor: '#da292e',
                confirmButtonColor: "#da292e"
            });
        }
    };

    const handleDelete = async () => {
        try {
            await deleteWhatsappLink(link.id);
            setLink(null);
            setUrl('');
            Swal.fire({
                title: 'Eliminado',
                text: 'El link de WhatsApp se eliminó correctamente.',
                icon: 'error',
                confirmButtonText: "Aceptar",
                iconColor: '#da292e',
                confirmButtonColor: "#da292e"
            });
        } catch (error) {
            console.error('Error al eliminar el link de WhatsApp:', error);
            Swal.fire({
                title: 'Error',
                text: 'Hubo un problema al eliminar el link.',
                icon: 'error',
                confirmButtonText: "Aceptar",
                iconColor: '#da292e',
                confirmButtonColor: "#da292e"
            });
        }
    };

    const confirmDelete = () => {
        Swal.fire({
            title: '¿Estás seguro?',
            text: 'Esta acción no se puede deshacer.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            iconColor: '#f4bd61',
            confirmButtonColor: '#2c4cc4',
            cancelButtonColor: '#da292e'
        }).then((result) => {
            if (result.isConfirmed) {
                handleDelete();
            }
        });
    };

    return (
        <Container className="mt-4">
            <div className="justify-content-start">
                <div>
                    <h2 className="fw-bold text-black">Gestor de Link de WhatsApp</h2>
                    <p>Añade el enlace del grupo de WhatsApp que deseas compartir.</p>

                    {/* Mostrar solo si hay un link disponible */}
                    <div className="mb-4">
                        {link ? (
                            <ListGroup>
                                <ListGroup.Item className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <strong>Link actual:</strong> <br />
                                        <a href={link.url} target="_blank" rel="noopener noreferrer">
                                            {link.url}
                                        </a>
                                    </div>
                                    <ButtonGroup>
                                        <Button variant="link" onClick={() => setEditing(true)} className="text-warning">
                                            <PencilFill size={18} />
                                        </Button>
                                        <Button variant="link" onClick={confirmDelete} className="text-danger">
                                            <TrashFill size={18} />
                                        </Button>
                                    </ButtonGroup>
                                </ListGroup.Item>
                            </ListGroup>
                        ) : (
                            <p>No hay enlace disponible. Añade uno nuevo.</p>
                        )}
                    </div>

                    {/* Mostrar el formulario de edición si 'editing' es verdadero */}
                    {editing && link ? (
                        <div>
                            <h5>Editar Link</h5>
                            <InputGroup className="mb-3">
                                <FormControl
                                    type="url"
                                    value={url}
                                    name="url"
                                    onChange={(e) => setUrl(e.target.value)}
                                    placeholder="URL del grupo de WhatsApp"
                                    required
                                />
                                <Button variant="primary" onClick={handleSubmit}>
                                    Actualizar
                                </Button>
                            </InputGroup>

                            <Button variant="secondary" onClick={() => setEditing(false)}>
                                Cancelar
                            </Button>
                        </div>
                    ) : (
                        // Si no estamos en modo de edición, mostrar el formulario para crear un nuevo link
                        !link && (
                            <div>
                                <h5>Nuevo Link</h5>
                                <InputGroup className="mb-3">
                                    <FormControl
                                        type="url"
                                        value={url}
                                        name="url"
                                        onChange={(e) => setUrl(e.target.value)}
                                        placeholder="URL del grupo de WhatsApp"
                                        required
                                    />
                                    <Button variant="danger" onClick={handleSubmit}>
                                        Crear
                                    </Button>
                                </InputGroup>
                            </div>
                        )
                    )}
                </div>
            </div>
        </Container>
    );
};

export default WhatsappLinkManager;
